import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock3 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { WeeklySlot } from '@/lib/publishQueues/types';
import { timeToMinutes, weeklySlotTimeLabel } from '@/lib/publishQueues/weeklySlots';
import { layoutCalendarSlots } from '@/lib/publishQueues/layoutCalendarSlots';

const HEIGHT = 180;
const MIN_BLOCK_HEIGHT = 6;

export function QueueMiniCalendar({ slots }: { slots: WeeklySlot[] }) {
  const { t, i18n } = useTranslation();
  const id = useId();
  if (slots.length === 0) return <p className="text-sm text-muted-foreground">{t('publishQueues.card.noSchedule')}</p>;
  const windows = slots.map((slot, index) => ({
    slot, index, start: timeToMinutes(slot.timeLocal), end: timeToMinutes(slot.endTimeLocal ?? slot.timeLocal),
  }));
  const firstHour = Math.floor(Math.min(...windows.map(({ start }) => start)) / 60);
  const lastHour = Math.ceil(Math.max(...windows.map(({ end }) => end)) / 60);
  // Round outward to two-hour boundaries so endpoint labels never crowd a nearby tick.
  const firstTick = Math.max(0, Math.floor((firstHour - 1) / 2) * 2);
  const lastTick = Math.min(24, Math.ceil((lastHour + 1) / 2) * 2);
  const spanHours = Math.max(6, lastTick - firstTick);
  const startHour = Math.max(0, Math.min(24 - spanHours, firstTick));
  const endHour = startHour + spanHours;
  const hourStep = spanHours > 12 ? 4 : 2;
  const ticks = [startHour, ...Array.from({ length: 24 }, (_, hour) => hour)
    .filter((hour) => hour > startHour && hour < endHour && hour % hourStep === 0), endHour];
  const position = (minute: number) => (minute - startHour * 60) / (spanHours * 60) * HEIGHT;
  const weekdays = Array.from({ length: 7 }, (_, weekday) => {
    const date = new Date(Date.UTC(2026, 7, 2 + weekday, 12));
    return { weekday,
      label: new Intl.DateTimeFormat(i18n.language, { weekday: 'long', timeZone: 'UTC' }).format(date),
      shortLabel: new Intl.DateTimeFormat(i18n.language, { weekday: 'short', timeZone: 'UTC' }).format(date).replace(/\.$/, ''),
    };
  });

  return (
    <div className="min-w-0 space-y-2">
      <div role="group" aria-label={t('publishQueues.schedule')}
        className="grid min-w-0 grid-cols-[2.25rem_repeat(7,minmax(0,1fr))] overflow-hidden rounded-xl border bg-background/50 pb-2 pr-1">
        <div aria-hidden="true" className="pt-7">
          <div className="relative text-[9px] tabular-nums text-muted-foreground" style={{ height: HEIGHT }}>
            {ticks.map((hour) => (
              <span key={hour} className="absolute right-1.5 -translate-y-1/2"
                style={{ top: Math.max(5, Math.min(HEIGHT - 5, position(hour * 60))) }}>
                {`${String(hour).padStart(2, '0')}:00`}
              </span>
            ))}
          </div>
        </div>
        {weekdays.map((day) => {
          const { blocks, laneCount } = layoutCalendarSlots(
            windows.filter(({ slot }) => slot.weekday === day.weekday), MIN_BLOCK_HEIGHT / HEIGHT * spanHours * 60,
          );
          const label = t('publishQueues.card.dayDetails', { day: day.label });
          return (
            <Popover key={day.weekday}>
              <PopoverTrigger asChild>
                <button type="button" disabled={blocks.length === 0} aria-label={label}
                  title={blocks.length ? `${day.label}: ${blocks.map(({ slot }) => weeklySlotTimeLabel(slot)).join(', ')}` : t('publishQueues.card.noSchedule')}
                  className="group min-w-0 rounded-md text-left transition-colors hover:bg-primary/5 focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-40">
                  <span className="flex h-7 items-center justify-center text-[10px] font-medium capitalize text-muted-foreground group-hover:text-foreground">{day.shortLabel}</span>
                  <span className="relative block border-l border-border/70" style={{ height: HEIGHT }}>
                    {ticks.map((hour) => <span key={hour} aria-hidden="true" className="absolute inset-x-0 border-t border-border/50" style={{ top: Math.min(HEIGHT - 1, position(hour * 60)) }} />)}
                    {blocks.map(({ index, start, end, lane }) => (
                      <span key={index} aria-hidden="true" data-mini-slot={index}
                        className="absolute rounded-sm border border-primary/45 bg-primary/25 transition-colors group-hover:bg-primary/40"
                        style={{ top: Math.min(position(start), HEIGHT - MIN_BLOCK_HEIGHT),
                          height: Math.max((end - start) / (spanHours * 60) * HEIGHT, MIN_BLOCK_HEIGHT),
                          left: `calc(${lane / laneCount * 100}% + 1px)`,
                          width: `max(2px, calc(${100 / laneCount}% - 3px))` }} />
                    ))}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent aria-labelledby={`${id}-${day.weekday}`} side="top" collisionPadding={12}
                className="max-h-[min(20rem,var(--radix-popover-content-available-height))] w-56 max-w-[calc(100vw_-_2rem)] overflow-y-auto p-3">
                <h4 id={`${id}-${day.weekday}`} className="mb-2 text-sm font-semibold capitalize">{day.label}</h4>
                <ul className="space-y-2">
                  {blocks.map(({ slot, index }) => (
                    <li key={index} className="flex items-center gap-2 text-sm tabular-nums">
                      <Clock3 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                      {weeklySlotTimeLabel(slot)}
                    </li>
                  ))}
                </ul>
              </PopoverContent>
            </Popover>
          );
        })}
      </div>
      <p className="text-[11px] leading-relaxed text-muted-foreground">{t('publishQueues.card.calendarHint')}</p>
    </div>
  );
}
